import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { toaster, useCopyToClipboard } from '@/shared/libs';
import { Button, Dialog, DialogFooter, Input, Label } from '@/shared/ui';
import { useCreateDealLinkMutation } from '@entities/Deal';
import { activeLegs, getOfferDisplayName } from '@entities/Offer';
import type { DealSource, Offer } from '@psp-aggregator/sdk';

export interface ProposalBuilderProps {
  offers: readonly Offer[];
  onClose: () => void;
  onCreated: () => void;
  source?: DealSource;
}

const PROPOSAL_RATE_KEY = {
  crypto_topup: 'crypto_top_up',
  payin: 'pay_in',
  payout: 'pay_out',
  settlement: 'settlement',
} as const;

export const ProposalBuilder: FC<ProposalBuilderProps> = ({
  offers,
  onClose,
  onCreated,
  source = 'agent_outbound',
}) => {
  const { t } = useTranslation();
  const copy = useCopyToClipboard();
  const mutation = useCreateDealLinkMutation();
  const [brand, setBrand] = useState('');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('');
  const [margins, setMargins] = useState<
    Record<string, Record<string, string>>
  >({});
  const [url, setUrl] = useState('');

  const submit = async (): Promise<void> => {
    if (!brand.trim() || !country.trim() || !currency.trim()) return;
    try {
      const link = await mutation.mutateAsync({
        items: offers.map((offer) => ({
          agent_margins: margins[offer.id] ?? {},
          offer_id: offer.id,
        })),
        merchant_brand: brand.trim(),
        merchant_country: country.trim(),
        merchant_currency: currency.trim(),
        source,
      });

      setUrl(link.url);
      onCreated();
      toaster.success({ title: t('toast.dealLinkGenerated') });
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.deals.actionFailed'))
          : t('errors.deals.actionFailed'),
      });
    }
  };

  return (
    <Dialog
      title={t('pages.agent.proposal.title')}
      width={720}
      onClose={onClose}>
      <div className="max-h-[min(65dvh,calc(90dvh-10rem))] space-y-4 overflow-y-auto overscroll-contain pr-1">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="proposal-brand">
              {t('pages.deals.fields.brand')}
            </Label>
            <Input
              id="proposal-brand"
              value={brand}
              onChange={(event) => setBrand(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="proposal-country">
              {t('pages.deals.fields.country')}
            </Label>
            <Input
              id="proposal-country"
              value={country}
              onChange={(event) => setCountry(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="proposal-currency">
              {t('pages.deals.fields.currency')}
            </Label>
            <Input
              id="proposal-currency"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
            />
          </div>
        </div>
        {offers.map((offer) => (
          <div key={offer.id} className="rounded-xl border border-border p-4">
            <p className="mb-3 font-semibold">{getOfferDisplayName(offer)}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {activeLegs(offer).map((leg) => {
                const rateKey = PROPOSAL_RATE_KEY[leg];

                return (
                  <div key={rateKey}>
                    <Label htmlFor={`${offer.id}-${rateKey}`}>
                      {t('pages.agent.proposal.margin', { rate: rateKey })}
                    </Label>
                    <Input
                      id={`${offer.id}-${rateKey}`}
                      inputMode="decimal"
                      value={margins[offer.id]?.[rateKey] ?? ''}
                      onChange={(event) =>
                        setMargins((current) => ({
                          ...current,
                          [offer.id]: {
                            ...current[offer.id],
                            [rateKey]: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {!!url && (
          <div className="rounded-xl bg-muted p-4">
            <Label htmlFor="proposal-url">
              {t('pages.agent.proposal.link')}
            </Label>
            <div className="mt-1 flex min-w-0 flex-col gap-2 sm:flex-row">
              <Input
                className="min-w-0 flex-1"
                id="proposal-url"
                readOnly
                value={url}
              />
              <Button
                className="shrink-0"
                type="button"
                onClick={() => void copy(url)}>
                {t('buttons.copy')}
              </Button>
            </div>
          </div>
        )}
      </div>
      <DialogFooter
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.close')}
            </Button>
            {!url && (
              <Button
                disabled={
                  mutation.isPending ||
                  !brand.trim() ||
                  !country.trim() ||
                  !currency.trim()
                }
                type="button"
                variant="brand"
                onClick={() => void submit()}>
                {t('pages.agent.proposal.create')}
              </Button>
            )}
          </>
        }
      />
    </Dialog>
  );
};

ProposalBuilder.displayName = 'ProposalBuilder';
